<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import format from 'date-fns/format';
import LineChart from 'shared/components/charts/LineChart.vue';
import { DEFAULT_CHART, DEFAULT_LINE_CHART } from '../constants';

const props = defineProps({
  history: {
    type: Array,
    default: () => [],
  },
});

const { t } = useI18n();

const points = computed(() =>
  (props.history || [])
    .filter(point => point?.scored_at != null && point?.b != null)
    .map(point => ({
      at: new Date(point.scored_at),
      b: Number(point.b),
    }))
    .sort((a, b) => a.at - b.at)
);

const collection = computed(() => ({
  labels: points.value.map(point => format(point.at, 'dd-MMM')),
  datasets: [
    {
      ...DEFAULT_LINE_CHART,
      label: t('BUZZ_REPORTS.SCORE'),
      data: points.value.map(point => point.b),
      borderWidth: 2,
      pointRadius: 4,
      pointHoverRadius: 6,
      tension: 0.3,
    },
  ],
}));

const chartOptions = computed(() => ({
  scales: {
    x: {
      display: true,
      ticks: {
        fontFamily: DEFAULT_CHART.scales.x.ticks.fontFamily,
        autoSkip: true,
        maxRotation: 0,
        maxTicksLimit: Math.min(points.value.length, 8),
      },
      grid: {
        drawOnChartArea: false,
      },
      border: {
        display: true,
      },
      title: {
        display: true,
        text: t('BUZZ_REPORTS.HISTORY_DATE'),
        font: { family: DEFAULT_CHART.scales.x.ticks.fontFamily },
      },
    },
    y: {
      type: 'linear',
      position: 'left',
      min: 0,
      max: 100,
      ticks: {
        fontFamily: DEFAULT_CHART.scales.y.ticks.fontFamily,
        stepSize: 25,
      },
      grid: {
        drawOnChartArea: false,
      },
      border: {
        display: true,
      },
      title: {
        display: true,
        text: t('BUZZ_REPORTS.SCORE'),
        font: { family: DEFAULT_CHART.scales.y.ticks.fontFamily },
      },
    },
  },
}));
</script>

<template>
  <div v-if="!points.length" class="text-sm text-n-slate-10">
    {{ t('BUZZ_REPORTS.NO_HISTORY') }}
  </div>
  <div v-else class="flex items-center justify-center h-72">
    <LineChart :collection="collection" :chart-options="chartOptions" />
  </div>
</template>
